import { Fetcher, objectToFormData, StrictFetcher } from '@this/src/util';
/* eslint-disable max-lines */
import _ from 'lodash';
import type { FlightType, PackageSearchQueryItems } from '@this/domain/search_query';
import type { TransitSearchApi } from '@this/domain/organization/organization2';
import { ALL_CABIN } from '@this/domain/search_query';
import type HotelStayPlan from '@this/domain/hotel/hotel_stay_plan';
import type { AvailableRooms } from '@this/domain/hotel/available_rooms';
import type SearchQueryItem from './search_query_item';
import type { Airline } from './select_store';
import type SelectStore from './select_store';
import type { AirportSet } from './flight/flight_list';
import FlightList from './flight/flight_list';
import HotelList from './hotel/hotel_list';
import TransitList from './transit/transit_list';
import type { SearchQueryItemJson } from './search_query_item';
import type { MemberJson } from './user/user_json';
import {
  isAnaFlightNumber,
  isJalFlightNumber,
  isSameDayRestrictedTicket,
  isStandardTicketType
} from './domestic_air/same_day_restricted_ticket';
import { resolveDomesticAirFareName } from './domestic_air/fare_detail_master';

export { isAnaFlightNumber, isSameDayRestrictedTicket, isStandardTicketType };

export interface MarginTypeJson {
  // MarginType.rb
  id: number;
  base_type: 'fixed' | 'percentage';
  base_amount: number;
  organization_id: number;
}

export interface SelectResponse {
  id: number;
  signed_in: boolean;
  show_fee: boolean;
  include_tax: boolean;
  margin_types: MarginTypeJson[];
  search_query_items: SearchQueryItemJson[];
  transport_available: boolean;
  hotel_available: boolean;
  traveler_representative: MemberJson;
  default_seat_classes?: string[];
  no_card_error: boolean;
}

export interface LegJson {
  name: string;
  from: {
    code: string | null;
    date: string;
    name: string;
    time: string;
    time_full: string;
  };
  to: {
    code: string | null;
    date: string;
    name: string;
    time: string;
    time_full: string;
  };
  text: string;
  type: string | null;
  airline_name: string | null;
  code_share: string | null;
}

export interface LegSummaryJson {
  type: string;
  indexes: string[];
  from: {
    name: string;
    express_station: string | null;
    time: string;
  };
  to: {
    name: string;
    express_station: string | null;
    time: string;
  };
  name: string | null;
  price: number | null;
  prices: TransitPriceJson[] | null;
  stations: number | null;
  totaltime: string | null;
  transitcount: number | null;
}

export interface SegmentJson {
  // Transport::Segment.rb
  legs: LegJson[];
  leg_summaries: LegSummaryJson[] | null;
  old_price: number | null;
  price: number | null;
  changeable_price: number | null;
  unchangeable_price: number | null;
  changeable_seat_type: string | null;
  unchangeable_seat_type: string | null;
  changeable_available_seat_count: string | number | null;
  unchangeable_available_seat_count: string | number | null;
  type: 'air' | 'shin' | 'express';
  boarding?: unknown;
  arrival?: unknown;
  distance: string | null;
  route_time: string | null;
  price_expired?: boolean;
  sources?: string[] | null;
}

export interface TransitPriceJson {
  flight_number?: string;
  price: number | null;
  seat_type: string;
  ticket_category: string;
  ticket_type: string | null;
  seat_count: string | number;
  price_expired?: boolean;
  connecting_group?: string | null;
}

export interface TransitJson {
  // Transit.rb
  id: string;
  price: number | null;
  price_range: string | null;
  price_text: string | null;
  prices: TransitPriceJson[] | null;
  changeable_price: number | null;
  changeable_price_text: string | null;
  changeable_pre_sale: boolean;
  unchangeable_price: number | null;
  unchangeable_pre_sale: boolean;
  routes: string[] | null;
  text: string;
  selected_text: string;
  legsummaries_fromto_jrair: string | null;
  legsummaries_fromto_before: string | null;
  legsummaries_fromto_after: string | null;
  legsummaries_typeprice_before: legsummaryesTypeprice | null;
  legsummaries_typeprice_after: legsummaryesTypeprice | null;
  legs_fromto: string;
  time: string | null;
  departure_time: string | null;
  arrival_time: string | null;
  segments: SegmentJson[];
  url: string | null;
  shinkansen: boolean;
  shinkansen_too_late: boolean | null;
  shinkansen_deadline: string | null;
  air_too_late: boolean | null;
  air: boolean;
  airport?: string | null;
  changeable_air: null;
  domestic_air_price_index?: number | null;
  selected_price: number;
  fee: number;
  peoplenum: number;
  search_query_id: number;
  search_query_item_id: number | null;
  optimal: boolean | null;
  package: true | null;
  additional_price: null;
  unchangeable_price_text?: string | undefined;
  is_rakuten_package: boolean | null;
  railway_distance?: number | null;
  is_unreserved_seat_available: boolean | null;
  flight_names: string[] | null;
  shinkansen_vacancy_status: string | null;
  // 当日予約許可組織向け。null/undefinedは当日予約非対象組織（従来挙動）
  same_day_selectable?: boolean | null;
  same_day_restricted_ticket_window?: boolean | null;
}

export interface legsummaryesTypeprice {
  type?: string;
  price?: number;
}

// 行き・帰り、出発・到着の航空便コード。
// 楽天パッケージのみで使う。複数拠点は考慮していない。
export interface RakutenPackageAirCode {
  outwardDeparture: string;
  outwardArrival: string;
  homewardDeparture: string;
  homewardArrival: string;
}

export interface TransportSearchResultJson {
  // Transport::TransportSearchResult.rb
  outword: TransitJson[];
  homeword: null;
  changeable_air?: boolean;
  shinkansen_vacancy_enabled?: boolean;
}

export interface TransitResponse extends TransportSearchResultJson {
  sameStation?: string;
}

export type SupplierDomain = 'domestic' | 'foreign';

export interface HotelJson {
  version?: string | null;
  score_index: number | null;
  id: string | null;
  hotel_id: string | number | null;
  search_query_id: number | null;
  search_query_item_id: number | null;
  name: string | null;
  address: string | null;
  price: number | null;
  price_detail: {
    nightly_rate: number;
    tax_and_service_fee?: number;
  } | null;
  price_detail_text: string | null;
  average_price: number | null;
  average_base_price: number | null;
  average_price_with_tax: number | null;
  average_base_price_with_tax: number | null;
  mandatory_tax: number | null;
  room_rate?: number | null;
  room_tax?: number | null;
  image: string | null;
  images: string[] | null;
  checkin: string | null;
  checkout: string | null;
  note?: string | null;
  checkin_date: string | null;
  checkout_date: string | null;
  walkminute?: string | null;
  latitude: number | null;
  longitude: number | null;
  distance: number | null;
  nearest_station_name: string | null;
  station_distance: number | null;
  bed_types: { description: string; id?: number }[] | null;
  breakfast: string | false | null;
  cancel_message: null;
  roomnum: string | null;
  stay_days: number | null;
  raw_hotel: never;
  raw_detail: never;
  raw_room: never;
  refundability_score: number | null;
  refund: string | null;
  refundable: boolean | null;
  query: never;
  room_type: string | null;
  room_smoke?: boolean | null;
  checkin_instructions: string | null;
  special_checkin_instrcutions: string | null;
  cancel_policy: string | null;
  property_amenities: string[] | null;
  room_amenities: string[] | null;
  sold_out: boolean | null;
  type: 'r' | 'e' | 'eps_rapid' | 'net_rate_eps_rapid' | 'mynavi' | 'new_rakuten' | 'agoda';
  operator_class_name: string | null;
  detail_path: string | null;
  reserve_url?: string | null;
  fee_ratio?: number | null;
  user_id?: number | null;
  package_type: 'FP' | null;
  package_base_price: number | null;
  too_late?: boolean | null;
  tel?: string;
  optimal?: boolean;
  rating: number | null;
  supplier_domain?: SupplierDomain;
  parking_information?: string | null;
  plan_name?: string;
  plan_detail?: string | null;
  selected_stay_plan_id: string;
  available_rooms?: AvailableRooms;
  stay_plans: Array<HotelStayPlan> | null;
  restricted: boolean;
  requires_manual_booking?: boolean;
  hotel_master_id?: number | null;
  google_reviews?: {
    rating: number | null;
    user_ratings_total: number | null;
    reviews: Array<{
      author_name: string;
      rating: number;
      text: string;
      relative_time_description: string;
      profile_photo_url: string;
    }> | null;
  } | null;
}

export type HotelScoreCategory =
  | 'price_diff_score'
  | 'distance'
  | 'station_distance'
  | 'refundability_score'
  | 'availability_score'
  | 'rating_score';

interface HotelScoreItem {
  name: string;
  price: number;
  distance: number | null;
  station_distance: number | null;
  refundability_score: number;
  availability_score: number;
  rating_score: number;
  booking_score: number;
  total_score: number;
  price_rank: number;
  distance_rank: number;
  station_distance_rank: number;
}

export interface HotelScore {
  is_foreign: boolean;
  selected_hotel: HotelScoreItem | null;
  hotel_scores: Partial<HotelScoreItem>[];
  hotel_count: number | null;
  average_price: number | null;
  average_distance: number | null;
  average_station_distance: number | null;
  top10_average_price: number | null;
  top10_average_distance: number | null;
  top10_average_station_distance: number | null;
}

export interface SlideSuggestion {
  search_query_item_id: number;
  station_id: number;
  area_name: string;
  prefecture_name: string;
  municipality_name: string;
  lat: number;
  lng: number;
  avg_price: number;
  min_price: number;
  hotel_count: number;
  price_diff?: number;
  transit: {
    from: string;
    to: string;
    minutes: number | null;
  };
  checkin_date: string;
  checkout_date: string;
  rooms: number;
  roomnum: number;
}

export interface HotelsResponse {
  search_result: {
    hotels: HotelJson[];
    prefecture_id: number | null;
    municipality_code: string | null;
    scores: HotelScore;
    score_reason_enabled: boolean;
    score_reason_reload: boolean;
  };
  price: number;
  price_limit_over_count: number | null;
  slide_suggestion: SlideSuggestion | null;
  filtered_by_organization_breakfast_flag: boolean;
  rakuten_static_file_updated_at: string | null;
}

export interface FlightSegmentJson {
  // Flight::FlightSegment.rb
  from: {
    code: string;
    date: string;
    datetime: string;
    name: string; // 発着地の名前。例: 東京
    time: string;
    time_zone: string;
  };
  to: {
    code: string;
    date: string;
    datetime: string;
    name: string;
    time: string;
    time_zone: string;
  };
  duration: null;
  marketing_carrier: string;
  operating_carrier: string;
  flight_number: string;
  booking_class: string;
  cabin: string;
  name: string;
  carrier_name: string;
  fare_basis_code?: string;
  air_route_detail?: AirRouteDetail;
  api_related_data: any;
}

export interface AirRouteDetail {
  cancel_policies: string[];
  change_reservation_policies: string[];
}

export interface FlightSliceJson {
  // Flight::FlightSlice.rb
  id: string;
  duration: string;
  duration_raw: number;
  segments: FlightSegmentJson[];
  deadline: null;
  time_to_live: string | null;
  raw_ticket_expired_at: string | null;
  ticketing_enable: boolean;
  description: string;
}

export interface FlightJson {
  // Flight::Flight.rb
  id: number;
  itineraries: FlightSliceJson[];
  outword: FlightSliceJson;
  homeword: FlightSliceJson | null;
  price: number;
  price_text: string;
  description: string;
  score: number;
  fee: number;
  peoplenum: number;
  search_query_id: number;
  search_query_item_id: number;
  ticketing_enable: boolean;
  seat_reservation_enable: boolean;
  minirule?: string;
}

export type FlightsResponse = {
  carrier_ids: string[];
  airports: AirportSet[];
  flights: FlightJson[];
  flight_type: 'N' | 'C' | '';
  applied_query_setting: { carrier: boolean; flight_type: boolean };
};

// 国内航空券の券種ラベル生成
// 予約前の（検索結果画面・予約確認画面）で共通利用される
export type DomesticAirTicketLabel = {
  ticketCategoryLabel: string;
  ticketTypeLabel: string | null | undefined;
  ticketLabel: string;
  isKabu: boolean;
  isStandard: boolean;
  isAna: boolean;
};

const ANA_FARE_LABEL_AVAILABLE_FROM = '2026-05-19';

const toDateString = (input: string | Date | null | undefined): string | null => {
  if (!input) return null;
  if (input instanceof Date) {
    const y = input.getFullYear();
    const m = String(input.getMonth() + 1).padStart(2, '0');
    const d = String(input.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  return typeof input === 'string' && input.length >= 10 ? input.slice(0, 10) : null;
};

// SkyTicket 由来の内部コード（'normal' / 'changeable_*' / 'unchangeable_*'）を
// ANA運賃ブランド名にマッピングして表示用に変換する
const mapTicketTypeForFare = (ticketType: string | null | undefined): string | null => {
  if (!ticketType) return null;
  if (ticketType === 'kabu' || ticketType.includes('株主優待')) return '株主優待割引';
  // 'unchangeable_' 判定を 'changeable_' より先に置く（順序を逆にすると誤マッチ）
  if (ticketType.startsWith('unchangeable_')) return 'シンプル';
  if (ticketType === 'normal' || ticketType.startsWith('changeable_')) return 'フレックス';
  return ticketType;
};

export const buildDomesticAirTicketLabel = (
  price: Pick<TransitPriceJson, 'ticket_type' | 'ticket_category' | 'flight_number'>,
  departureDate?: string | Date | null,
  flightNumber?: string | null
): DomesticAirTicketLabel => {
  // 「株主優待」はANA系・スクレイピング系、「株主割引」はJAL officialの生運賃名
  const isKabu =
    price.ticket_type === 'kabu' ||
    !!price.ticket_type?.includes('株主優待') ||
    !!price.ticket_type?.includes('株主割引');
  const isStandard = isStandardTicketType(price.ticket_type);

  const effectiveFlightNumber = price.flight_number || flightNumber || null;
  const isAna = isAnaFlightNumber(effectiveFlightNumber);
  const departureDateStr = toDateString(departureDate);
  const isOnOrAfterStart = !!departureDateStr && departureDateStr >= ANA_FARE_LABEL_AVAILABLE_FROM;
  const showFareLabel = isAna && isOnOrAfterStart;

  const ticketCategoryLabel =
    isStandard && showFareLabel
      ? '変更可（有料）'
      : price.ticket_category === 'changeable'
      ? '変更可'
      : '変更不可';

  // JAL便はマスタ収録運賃名（kabu/株主系→「株主優待割引」、生値一致→そのまま、非ヒット→null）を優先する。
  // ANA・その他キャリアの kabu は従来どおり「株主優待割引」
  const isJal = isJalFlightNumber(effectiveFlightNumber);
  const ticketTypeLabel = isJal
    ? resolveDomesticAirFareName('JAL', price.ticket_type, departureDate)
    : isKabu
    ? '株主優待割引'
    : showFareLabel
    ? mapTicketTypeForFare(price.ticket_type)
    : null;

  // 予約確認画面で表示するラベル（券種ラベル + 運賃名併記）
  // ANA(NH)は5/19以降のみ運賃名併記（従来どおり）。JALはマスタ運賃名がヒットした場合のみ併記。
  // それ以外は修正前の通り「変更可」「変更不可」のみ
  const showTicketTypeInLabel = isJal ? !!ticketTypeLabel : showFareLabel && !!ticketTypeLabel;
  const ticketLabel = showTicketTypeInLabel ? `${ticketCategoryLabel}（${ticketTypeLabel}）` : ticketCategoryLabel;

  return { ticketCategoryLabel, ticketTypeLabel, ticketLabel, isKabu, isStandard, isAna };
};

interface PackagesResponse {
  show: true;
  errors?: string[] | undefined;
  results?: (TransitJson[] | HotelJson[])[];
}

class SelectRepository {
  private store: SelectStore;

  constructor(args: { store: SelectStore }) {
    this.store = args.store;
  }

  async initPromise(query: any): Promise<void> {
    const result = await StrictFetcher.post<SelectResponse>(
      '/select.json',
      'select/create.json',
      objectToFormData({ search_date: Date.now(), ...query })
    );
    if (this.store.query.id && this.store.query.id !== result.id) {
      this.store.query.replaceItems(result.id, result.search_query_items);
      this.store.result.replaceItems(this.store.query.items);
    }
    this.store.query.setId(result.id, result.search_query_items);
    this.store.result.setSearchQueryId(result.id);
    this.store.result.setShowFee(result.show_fee);
    this.store.result.setIncludeTax(result.include_tax);
    this.store.result.setMarginTypes(result.margin_types);
    this.store.result.setTransportAvailable(result.transport_available);
    this.store.result.setHotelAvailable(result.hotel_available);
    this.store.result.setTravelerRepresentative(result.traveler_representative);
    this.store.result.setNoCardError(result.no_card_error);
    this.store.signedIn = result.signed_in;
  }

  async startSearch(themeClass?: string) {
    await this.initPromise(utils.getParams());
    const transitSearchApi = this.store.transitSearchApi();
    this.fetchElements(this.store.query.items, transitSearchApi);
    const packageItems = this.store.query.identifyPackageQueryItems();

    if (location.pathname === '/select' && themeClass && packageItems.length > 0) {
      await Promise.all([this.fetchPackages(packageItems[0]), this.fetchJrPackages(themeClass)]);
    } else {
      const message = [
        'ご指定の検索条件では出張パッケージの検索はできません',
        '出発地・目的地が一致する往復の航空券とホテルを指定して検索してください'
      ];
      this.store.packageErrors.ANA = message;
      this.store.packageErrors.JAL = message;
      this.store.jrPackageErrors = message;
      this.store.packageLoading.ANA = false;
      this.store.packageLoading.JAL = false;
      this.store.jrPackageLoading = false;
    }
  }

  async reSearchHotel(lati: number, lag: number, targetItem: SearchQueryItem) {
    targetItem.reFetchHotels(lati, lag);
  }

  fetchPackages(packageItem: PackageSearchQueryItems) {
    const outFlightName = packageItem.origin.flightNum;
    const homeFlightName = packageItem.destination.flightNum;
    const { hotelName } = packageItem.hotel;

    this.fetchRakutenPackages({
      packageItem,
      outFlightName,
      homeFlightName,
      hotelName,
      firstSearch: true
    });
  }

  // 楽天パッケージから行き・帰りの航空便、ホテルの情報を取得する。
  //
  // @param {Object} opts オプション
  // @param {string} opts.outFlightName 絞り込みたい行きの航空便の名前。
  // @param {string} opts.homeFlightName 絞り込みたい帰りの航空便の名前。
  // @param {string} opts.airline 絞り込みたい航空種別。
  // @param {string} opts.hotelName 絞り込みたいホテル名。
  async fetchRakutenPackages(
    opts: {
      packageItem?: PackageSearchQueryItems;
      outFlightName?: string;
      homeFlightName?: string;
      airlines?: Airline[];
      hotelName?: string | null | undefined;
      airCode?: RakutenPackageAirCode | null;
      firstSearch?: boolean;
    } = {}
  ) {
    const airlines: Airline[] = utils.dig(opts, 'airlines') || ['JAL', 'ANA'];
    airlines.forEach(airline => {
      this.store.packageLoading[airline] = true;
    });

    const items = opts.packageItem || this.store.query.identifyPackageQueryItems()[0];
    const outwardTransitQueryItem: SearchQueryItem = items.origin;
    const homewardTransitQueryItem: SearchQueryItem = items.destination;
    if (opts) {
      if (opts.outFlightName) {
        outwardTransitQueryItem.flightNum = opts.outFlightName;
      }
      if (opts.homeFlightName) {
        homewardTransitQueryItem.flightNum = opts.homeFlightName;
      }
    }

    if (opts.hotelName) {
      const hotelQueryItem: SearchQueryItem = items.hotel || {};

      // ホテル名の一意性をある程度担保できそう、かつ、
      // パラメーターとしては長過ぎない文字数。
      const maxHotelNameLength = 20;

      const hotelName: string = _.truncate(opts.hotelName, { length: maxHotelNameLength, omission: '' });

      hotelQueryItem.hotelName = hotelName;
    }

    await Promise.all([
      SelectRepository.hotelGeocodesPromise(this.store.query.items),
      SelectRepository.transportGeocodesPromise(this.store.query.items)
    ]);

    if (this.store.query.items.some(item => !item.domestic)) {
      this.store.showPackage = false;
      airlines.forEach(airline => {
        this.store.packageLoading[airline] = false;
      });
      this.store.result.setType('separate');
      return;
    }

    let airCodeParams: Record<string, unknown> | null;
    if (opts && opts.airCode) {
      airCodeParams = {
        air_code: {
          // パラメーターの名前は短くする。
          out_dep: opts.airCode.outwardDeparture,
          out_arr: opts.airCode.outwardArrival,
          home_dep: opts.airCode.homewardDeparture,
          home_arr: opts.airCode.homewardArrival
        }
      };
    }

    const firstSearch = utils.dig(opts, 'firstSearch');

    await Promise.all(
      airlines.map(async airline => {
        const response = await Fetcher.get<PackagesResponse>(
          `/rakuten_packages`,
          Object.assign(
            this.store.query.rakutenPackageParams(),
            { search_date: Date.now(), airline, first_search: firstSearch },
            airCodeParams
          )
        );

        if (response) {
          if (response.results) {
            this.store.result.setPackageResult(response.results, 'rakuten', airline);
            this.fetchIndirectFlights(airline);
            const hotelResult = this.store.result.packageItems[airline].find(
              item => item.elementType() === 'hotel'
            );
            if (hotelResult && hotelResult.elementList instanceof HotelList && hotelResult.queryItem.destGeocode) {
              const { location } = hotelResult.queryItem.destGeocode;
              hotelResult.setDestLocation(location);
              hotelResult.elementList.fetchDistances(location);
            }
          } else if (response.errors) {
            this.store.packageErrors[airline] = response.errors;
          }
        }

        this.store.packageLoading[airline] = false;
        app.render();
      })
    );
  }

  fetchIndirectFlights(airline: Airline) {
    this.store.result.packageItems[airline].forEach(item => {
      const elementList = item.elementList;
      if (!(elementList instanceof TransitList)) return;

      const transitWithVia = elementList.list.filter(transit => {
        const segment = transit.segments[0];
        if (!segment) return false;

        const hasVia = transit.flightNames && transit.flightNames.length > 1; // 便名が二つ以上ある場合
        const via = segment.legs.filter(l => l.from.name === '' || l.to.name === '');
        return hasVia && via.length;
      });
      // 選択済みの経由便情報を最優先で取得したい
      const selectedIndex = transitWithVia.findIndex(t => t.id === elementList.currentId);
      if (selectedIndex >= 0) {
        [transitWithVia[0], transitWithVia[selectedIndex]] = [transitWithVia[selectedIndex], transitWithVia[0]];
      }
      transitWithVia.forEach(transit => {
        Fetcher.get<LegJson[]>('/rakuten_packages/via_flights', { transit_id: transit.id, airline }).then(res => {
          transit.segments[0].legs = res;
          app.render();
        });
      });
    });
  }

  async fetchJrPackages(themeClass: string) {
    if (themeClass !== 'mynavi') {
      await Promise.all([
        SelectRepository.hotelGeocodesPromise(this.store.query.items),
        SelectRepository.transportGeocodesPromise(this.store.query.items)
      ]);

      const response = await Fetcher.get<PackagesResponse>(`/jr_packages`, {
        search_date: Date.now(),
        ...this.store.query.jrPackageParams()
      });

      if (response) {
        if (response.results) {
          this.store.result.setJrPackageResult(response.results);

          const hotelResult = this.store.result.jrPackageItems.find(item => item.elementType() === 'hotel');
          if (hotelResult && hotelResult.elementList instanceof HotelList && hotelResult.queryItem.destGeocode) {
            hotelResult.elementList.fetchDistances(hotelResult.queryItem.destGeocode.location);
          }
        } else if (response.errors) {
          this.store.jrPackageErrors = response.errors;
        }
      }
    }

    this.store.jrPackageLoading = false;
    app.render();
  }

  fetchElements(items: SearchQueryItem[], transitSearchApi: TransitSearchApi) {
    SelectRepository.fetchElementHotels(items);
    // 新幹線残席表示は、出張代表者（出張者1人目）で判断する仕様のため、出張代表者（出張者1人目）の user_id を URLパラメータから抽出
    // 手入力（type==='companion'）の場合は null で手配者(current_user)にfallback
    const urlParams = new URLSearchParams(window.location.search);
    const firstUserId = urlParams.get('travelers[0][user_id]');
    const firstType = urlParams.get('travelers[0][type]');
    const travelerUserId =
      firstType && firstType !== 'companion' && firstUserId ? Number(firstUserId) || null : null;
    SelectRepository.fetchElementDomesticTransports(items, transitSearchApi, travelerUserId);
    this.fetchElementForeignTransports(items);
  }

  static async fetchElementHotels(items: SearchQueryItem[]) {
    const hotelItems = _.filter(items, item => item.itemType === 'hotel');
    _.each(hotelItems, item => item.fetchHotels());
  }

  static fetchElementDomesticTransports(
    items: SearchQueryItem[],
    transitSearchApi: TransitSearchApi,
    travelerUserId?: number | null
  ) {
    SelectRepository.transportGeocodesPromise(items).then(items => {
      items
        .filter(v => v.domestic)
        .forEach(item => {
          item.fetchDomesticTransits(transitSearchApi, travelerUserId);
        });
    });
  }

  static async fetchShinkansenVacancy(
    transitList: TransitList,
    date: string,
    travelerUserId?: number | null
  ): Promise<void> {
    const segments: Array<{
      transitIndex: number;
      segmentIndex: number;
      from: string;
      to: string;
      legs: Array<{ name: string; type: string | null; from: string; to: string }>;
    }> = [];

    transitList.list.forEach((transit, tIdx) => {
      transit.segments.forEach((segment, sIdx) => {
        if (segment.type === 'shin') {
          if (segment.legs.length === 0) return;
          segments.push({
            transitIndex: tIdx,
            segmentIndex: sIdx,
            from: segment.legs[0]?.from?.name ?? '',
            to: segment.legs[segment.legs.length - 1]?.to?.name ?? '',
            legs: segment.legs
              .filter(l => l.type === 'shin')
              .map(l => ({ name: l.name, type: l.type, from: l.from?.name ?? '', to: l.to?.name ?? '' }))
          });
        }
      });
    });

    if (segments.length === 0) return;

    transitList.shinkansenVacancyLoading = true;
    transitList.list.forEach(transit => {
      if (transit.segments.some(s => s.type === 'shin')) {
        transit.shinkansenVacancyLoading = true;
      }
    });
    app.render();

    try {
      const requestBody = {
        date,
        traveler_user_id: travelerUserId,
        segments: segments.map(s => ({
          from: s.from,
          to: s.to,
          legs: s.legs
        }))
      };
      const response = await Fetcher.post<{
        results: Array<{ status: string | null; legs: Array<{ name: string; status: string | null }> }>;
      }>('/shinkansen_vacancy.json', requestBody);

      response.results.forEach((result, idx) => {
        const seg = segments[idx];
        if (!seg) return;
        const transit = transitList.list[seg.transitIndex];
        const segment = transit.segments[seg.segmentIndex];

        segment.shinkansenVacancyStatus = result.status;
      });

      // transit単位の集約
      transitList.list.forEach((transit, ti) => {
        const statuses = transit.segments
          .map(s => s.shinkansenVacancyStatus)
          .filter((s): s is string => s != null);
        if (statuses.length > 0) {
          transit.shinkansenVacancyStatus = SelectRepository.worstVacancyStatus(statuses);
        }
      });

      const currentTransit = transitList.list.find(t => t.id === transitList.currentId);
      if (currentTransit && currentTransit.segments.some(s => s.isShinkansenUnavailable())) {
        const nextShin = transitList.list.find(
          t => t.segments.some(s => s.type === 'shin') && !t.segments.some(s => s.isShinkansenUnavailable())
        );
        const nextAvailable = nextShin ?? transitList.list.find(t => !t.segments.some(s => s.type === 'shin'));
        if (nextAvailable) {
          transitList.currentId = nextAvailable.id;
        }
      }
    } catch (error) {
      console.warn('[fetchShinkansenVacancy] Failed:', error);
    } finally {
      transitList.shinkansenVacancyLoading = false;
      transitList.list.forEach(transit => {
        transit.shinkansenVacancyLoading = false;
      });
      app.render();
    }
  }

  private static worstVacancyStatus(statuses: string[]): string {
    const priority = ['unavailable', 'unavailable_with_free', 'few', 'unknown', 'available'];
    const found = priority.find(p => statuses.includes(p));
    return found ?? statuses[0] ?? 'unknown';
  }

  fetchElementForeignTransports(items: SearchQueryItem[]) {
    const maxSegments = this.store.maxSegments();

    SelectRepository.transportGeocodesPromise(items).then(items => {
      const foreignItems = items.filter(item => !item.domestic);

      SelectRepository.groupItems(foreignItems, maxSegments).forEach(items => {
        this.fetchForeignFlights(items);
      });
    });
  }

  /*
   * Amadeusの場合は3区間毎、Travelportの場合は6区間毎にPNRをまとめる
   * https://aitravel.atlassian.net/browse/AITRAVEL-3846
   */
  static groupItems(items: SearchQueryItem[], maxSegments: number): SearchQueryItem[][] {
    return _.reduce(
      items,
      (res: SearchQueryItem[][], item) => {
        const currentLength = res.length;
        if (currentLength === 0 || res[currentLength - 1].length === maxSegments) {
          res[currentLength] = [];
          res[currentLength].push(item);
        } else {
          res[currentLength - 1].push(item);
        }
        return res;
      },
      []
    );
  }

  async fetchForeignFlights(items: SearchQueryItem[]) {
    let params_cabin: any;
    const isDefaultSeatClassesIncludeAllCabin = ALL_CABIN.every(cabin =>
      this.store.result.query.defaultSeatClasses.includes(cabin)
    );
    if (this.store.query.cabin[0] === 'all') {
      if (isDefaultSeatClassesIncludeAllCabin) {
        params_cabin = '';
      } else {
        params_cabin = _.cloneDeep(this.store.result.query.defaultSeatClasses).join(',');
      }
    } else if (this.store.query.cabin.length === 0) {
      if (isDefaultSeatClassesIncludeAllCabin) {
        params_cabin = '';
      } else {
        params_cabin = _.cloneDeep(this.store.result.query.defaultSeatClasses).join(',');
      }
    } else if (ALL_CABIN.every(cabin => this.store.query.cabin.includes(cabin))) {
      params_cabin = '';
    } else {
      params_cabin = this.store.query.cabin.join(',');
    }
    const params = {
      search_date: Date.now(),
      type: 'circuit',
      queries: _.map(items, item => item.flightParams()),
      peoplenum: this.store.query.peoplenum,
      cabin: params_cabin,
      flight_type: this.store.query.flightType
    };
    const res = await Fetcher.get<FlightsResponse>(`/flights`, params, { arrayFormat: 'indices' });
    const list = new FlightList(res.flights, {
      showFee: this.store.result.showFee,
      marginType: this.store.result.marginTypes.foreignMarginType(),
      current: items[0].selectedElement,
      cabins:
        this.store.query.cabin[0] === 'all'
          ? _.cloneDeep(this.store.result.query.defaultSeatClasses)
          : this.store.query.cabin,
      handleChange: SelectRepository.foreignQueryItems(items)[0].resultItem.handleChange || undefined
    });
    SelectRepository.foreignQueryItems(items).forEach((item, i) => {
      if (item.resultItem) {
        item.resultItem.queryItem.airports = res.airports[i];
        item.resultItem.setFlightIndex(i);
        item.resultItem.setFlightList(list);
        item.resultItem.setLoading(false);
        item.setCarrierIds(res.carrier_ids.length ? res.carrier_ids : ['all']);
      }
    });
    list.firstHandleChange();
    this.store.query.flightType = res.flight_type;
    this.store.appliedFlightQuerySetting = res.applied_query_setting;
  }

  getPrefecture(i: number) {
    if (this.store.query.items[i].domestic) {
      return (
        (this.store.query.items[i].destGeocode && this.store.query.items[i].destGeocode?.prefectureName) || ''
      );
    }
    return '';
  }

  static foreignQueryItems(items: SearchQueryItem[]): SearchQueryItem[] {
    return _.filter(items, item => item.itemType === 'transport' && !item.domestic);
  }

  static transportGeocodesPromise(items: SearchQueryItem[]) {
    const transportItems = _.filter(items, item => item.itemType === 'transport');
    const transportPromises = _.map(transportItems, item => item.transportGeocodesPromise());
    return Promise.all(transportPromises);
  }

  static hotelGeocodesPromise(items: SearchQueryItem[]) {
    const transportItems = _.filter(items, item => item.itemType === 'hotel');
    const transportPromises = _.map(transportItems, item => item.hotelGeocodesPromise());
    return Promise.all(transportPromises);
  }

  updateCabin = (cabins: string[]) => {
    this.store.query.setCabin(cabins);
  };

  updateCarrierIds = (carrierIds: string[]) => {
    this.store.query.setCarrierList(this.store.query.carrierList || this.store.result.flightList!.carrierList);
  };

  updateFlightType = (flightType: FlightType) => {
    this.store.query.setFlightType(flightType);
  };

  reloadFlights() {
    this.store.setTab('none');
    const foreignQueryItems = SelectRepository.foreignQueryItems(this.store.query.items);
    _.each(foreignQueryItems, item => item.resultItem.setLoading(true));
    this.store.pushHistory();
    this.fetchElementForeignTransports(foreignQueryItems);
  }

  startPartialSearch(item: SearchQueryItem) {
    const itemParams = item.getSubmitParams();
    const query = {
      search_query_id: this.store.query.id,
      items: { '0': itemParams }
    };
    this.initPromise(query).then(
      () => {
        const transitSearchApi = this.store.transitSearchApi();
        this.fetchElements([item], transitSearchApi);
      },
      (error: any) => {
        this.store.setBasicError();
      }
    );
  }
}

export default SelectRepository;
